<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <div class="page-header-with-filters">
      <d-row
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">
            {{ $t('container.containersOrders') }}
          </span>
          <h3 class="page-title">
            {{ $t('container.reservation.containersReservationsList') }}
          </h3>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'containers-reservations'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('product.product') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px;">{{ $t('ui.order') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px">{{ $t('container.container') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 150px;">{{ $t('ui.amount') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(reservation, index) of reservations"
            :key="'reservation-' + index">
            <td class="align-middle">
              {{ reservation.product.name }}<br>
              <small>{{ $t('product.productCode') }}: <strong>{{ reservation.product.code }}</strong></small>
            </td>
            <td class="align-middle">
              <template v-if="reservation.order_product">
                <router-link :to="'/zamowienia/edycja/' + reservation.order_product.order.id + '/z-rezerwacji'">
                  {{ reservation.order_product.order.order_name }}
                </router-link>
              </template>
            </td>
            <td class="align-middle">
              <router-link :to="'/kontenery/edycja/' + reservation.container.id + '/z-rezerwacji'">
                {{ reservation.container.number }}
              </router-link>
              <br>
              <small>
                {{ $t('ui.deliveryDate') }}:
                <strong>
                  {{ reservation.container.date }}
                </strong>
              </small>
            </td>
            <td class="align-middle">
              {{ reservation.container_quantity }} {{ $t('ui.piecesAbbr') }}
            </td>
          </tr>
          <tr v-if="reservations.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('container.reservation.noContainersReservationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'containers-reservations',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedContainers && this.loadedProducts;
    },
    productOptions () {
      let options = [];

      for (let i = 0; i < this.products.length; i++) {
        options.push({
          text: this.products[i].code,
          value: this.products[i].id
        });
      }
      return options;
    },
    containerOptions () {
      let options = [];

      for (let i = 0; i < this.containers.length; i++) {
        options.push({
          text: `${this.containers[i].number} (${this.containers[i].date})`,
          value: this.containers[i].id
        });
      }
      return options;
    },
    containerStatusOptions () {
      return [
        {
          text: this.$t('container.reservation.reservationsOpen'),
          value: 'OPEN'
        },
        {
          text: this.$t('container.reservation.reservationsClose'),
          value: 'CLOSED'
        }
      ];
    },
    filtersConfig () {
      return [
        {
          key: 'order',
          apiKey: 'order',
          label: this.$t('ui.order'),
          placeholder: this.$t('order.enterOrderNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'product_id',
          apiKey: 'product_id',
          label: this.$t('product.selectProduct'),
          placeholder: this.$t('product.selectProduct'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.productOptions
        },
        {
          key: 'container_id',
          apiKey: 'container_id',
          label: this.$t('container.selectContainer'),
          placeholder: this.$t('container.selectContainer'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.containerOptions
        },
        {
          key: 'container_status',
          apiKey: 'container_status',
          label: this.$t('status.statusQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: 'OPEN',
          options: this.containerStatusOptions
        }
      ];
    }
  },
  data () {
    return {
      reservations: [],
      loaded: false,
      loadedContainers: false,
      loadedProducts: false,
      loadError: false,
      containers: [],
      products: [],
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      if (!where.container_status) {
        where.container_status = 'OPEN';
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/products/containers/reservations?page=' + this.currentPage,
        listField: 'reservations',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/containers/items?return=get&where[status]=OPEN',
        loadedKey: 'loadedContainers',
        outputKey: 'containers',
        errorKey: 'loadError'
      });
    }
  }
};
</script>
